import React, { useEffect, useMemo } from "react";
import { Button, Modal, Row, Typography, Form, FormInstance, Col, Input, Select, Table, Space, TableProps, InputNumber } from "antd";
import FormLabel from "src/components/AntdCustomPart/FormLabel/FormLabel";
import { generateErrorMessage } from "src/utils/error-message";
import { Composition, CompositionDropdown } from "src/entity/flare/flare.constants";
import { onKeyDownNumberWithDecimal, onKeyDownNumberWithFixedDecimal } from "src/utils/key-down-validate";
import { CompoundUnits, UNITS_ENUM } from "src/entity/common/unit.constant";
import { flareHeatingStore } from "src/stores/flare-heating-store";
import { useShallow } from "zustand/react/shallow";
import { BsTrashFill } from "react-icons/bs";
import { ResponseSettingFlareCompound } from "src/entity/settings/flare-compound";
import { ManageCompoundProps } from "src/entity/flare/heating-value.entity";
import WrapCenter from "src/components/WrapCenter/WrapCenter";
import { PlusCircleOutlined } from "@ant-design/icons";
import { valueType } from "antd/es/statistic/utils";
import { debounce } from "lodash";
import CustomInputNumber from "src/components/AntdCustomPart/InputNumber/CustomInputNumber";
import { formatNumber } from "src/utils/process-number";

const { Text } = Typography;

interface HeatingModalProps {
  submitSetupHeatingValue: () => void;
  loadingManageHeating: boolean;
  visibleModal: boolean;
  closeModal: () => void;
  errorHeating: string | null;
  formSetupHeatingValue: FormInstance;
  onAddCompound: () => void;
  onDeleteManageCompound: (key: string | number) => void;
  manageCompound: ManageCompoundProps[];
  calculateHeatingValue: (data: ManageCompoundProps[]) => void;
  onUpdateKeyVal: (rowKey: string, field: keyof ManageCompoundProps, value: string | boolean) => void;
  setManageCompound: (data: ManageCompoundProps[]) => void;
  clearErrorHeating: (key: string) => void;
}

const wrapper_form = {
  marginBottom: 0,
};
const getWrapperSlotStyle = () => {
  return {
    ...wrapper_form,
  };
};

const HeatingModal: React.FC<HeatingModalProps> = (props) => {
  const loadingCompound = flareHeatingStore(useShallow((state) => state.loadingCompound));
  const dataCompound = flareHeatingStore(useShallow((state) => state.dataCompound));
  const {
    submitSetupHeatingValue,
    loadingManageHeating,
    closeModal,
    visibleModal,
    errorHeating,
    formSetupHeatingValue,
    onAddCompound,
    onDeleteManageCompound,
    manageCompound,
    onUpdateKeyVal,
    calculateHeatingValue,
    setManageCompound,
    clearErrorHeating,
  } = props;

  const optionsCompound = useMemo(() => {
    return dataCompound
      .map((item) => ({
        label: item.compound,
        value: item._id,
      }))
      .sort((a, b) => {
        // Use a regular expression to remove numbers and special characters at the beginning of the string
        const cleanNameA = a.label.replace(/^[^a-zA-Z]+/, "");
        const cleanNameB = b.label.replace(/^[^a-zA-Z]+/, "");

        // Compare the cleaned names
        return cleanNameA.localeCompare(cleanNameB);
      });
  }, [dataCompound]);

  const composition = Form.useWatch("composition", formSetupHeatingValue);
  const heatingValueUnit = Form.useWatch("unitHeatingValue", formSetupHeatingValue);
  // console.log("Data form : ", { composition, heatingValueUnit });
  // console.log("formSetupHeatingValue : ", formSetupHeatingValue.getFieldsValue());
  const onChangeCompound = (val: string, key: string) => {
    const findValue = dataCompound.find((item) => item._id === val);
    const resultValue = heatingValueUnit && heatingValueUnit == UNITS_ENUM.KCAL_KG ? findValue?.kcalKg : findValue?.kcalM3;
    formSetupHeatingValue.setFieldValue(`heatingValue-${key}`, resultValue);
    onUpdateKeyVal(key, "heatingValue", resultValue as string);
    onUpdateKeyVal(key, "compound", val);
    formSetupHeatingValue.validateFields({ dirty: true }).catch(() => console.log());
  };

  const handleMoleChange = (rowKey: string, field: keyof ManageCompoundProps, value: string | boolean) => {
    onUpdateKeyVal(rowKey, field, value);
  };

  const debounceInputMole = debounce(handleMoleChange, 500);

  const onChangeComposition = (val: string) => {
    if (val === Composition.SPECIFIC_COMPOSITION) formSetupHeatingValue.setFieldValue("netHeatingValue", "");
    else {
      formSetupHeatingValue.setFieldValue("netHeatingValue", undefined);
      setManageCompound([]);
    }
  };

  const validateIsDuplicateCompound = (_: any, value: any) => {
    if (manageCompound.filter((item) => item.compound === value).length > 1) return Promise.reject("Duplicate compound.");
    else return Promise.resolve();
  };

  const columns: TableProps<ManageCompoundProps>["columns"] = [
    {
      title: "No",
      key: "no",
      render: (_, __, index) => index + 1,
      align: "center",
    },
    {
      title: "Compound",
      key: "compound",
      render: (_, record) => (
        <Form.Item
          style={getWrapperSlotStyle()}
          name={"compound-" + record.key}
          rules={[
            { required: true, message: generateErrorMessage("Compound") },
            {
              validator: validateIsDuplicateCompound,
            },
          ]}
        >
          <Select
            showSearch
            filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
            options={optionsCompound}
            onChange={(val) => onChangeCompound(val, record.key)}
          />
        </Form.Item>
      ),
      align: "center",
    },
    {
      title: "Heating Value",
      key: "heatingValue",
      render: (_, record) => (
        <Form.Item
          style={getWrapperSlotStyle()}
          name={"heatingValue-" + record.key}
          rules={[{ required: true, message: generateErrorMessage("Heating Value") }]}
        >
          <InputNumber
            disabled
            onChange={(e) => onUpdateKeyVal(record.key, "heatingValue", `${e}`)}
            formatter={(value) => formatNumber(value as number)}
          />
        </Form.Item>
      ),
      align: "center",
    },
    {
      title: "Mole (%)",
      key: "mole",
      render: (_, record) => (
        <Form.Item
          style={getWrapperSlotStyle()}
          name={"mole-" + record.key}
          rules={[{ required: true, message: generateErrorMessage("Mole") }]}
        >
          {/* <InputNumber
            max={100}
            onChange={(e) => debounceInputMole(record.key, "mole", JSON.stringify(e))}
          /> */}
          <CustomInputNumber precision={2} max={100} onChange={(e) => debounceInputMole(record.key, "mole", JSON.stringify(e))} />
        </Form.Item>
      ),
      align: "center",
    },
    {
      title: "Action",
      key: "action",
      render: (_, record, index) => (
        <Space size="middle" direction={"horizontal"}>
          <Button
            className="border-transparent bg-transparent"
            size={"small"}
            onClick={() => onDeleteManageCompound(index)}
            disabled={false}
          >
            <BsTrashFill size={18} />
          </Button>
        </Space>
      ),
      align: "center",
    },
  ];

  const onChangeUnitHeatingValue = (val: string) => {
    const currentValues = formSetupHeatingValue.getFieldsValue();
    const relatedKeysCompound = Object.keys(currentValues).filter((item) => item.includes("compound-"));
    const relatedKeysHeating = Object.keys(currentValues).filter((item) => item.includes("heatingValue-"));
    const tmpManageCompound = [...manageCompound];
    relatedKeysCompound.forEach((item) => {
      const splitKey = item.split("_");
      const extractId = splitKey.slice(1).join("_");
      const findValue = dataCompound.find((e) => e._id === currentValues[item]);
      const findHeatingKey = relatedKeysHeating.find((heatingKey) => heatingKey.includes(extractId));
      if (val === UNITS_ENUM.KCAL_KG) {
        tmpManageCompound.forEach((tmpCompound) => {
          if (tmpCompound.compound === currentValues[item]) tmpCompound.heatingValue = findValue?.kcalKg as string;
        });
        formSetupHeatingValue.setFieldValue(findHeatingKey, findValue?.kcalKg);
      } else {
        tmpManageCompound.forEach((tmpCompound) => {
          if (tmpCompound.compound === currentValues[item]) tmpCompound.heatingValue = findValue?.kcalM3 as string;
        });
        formSetupHeatingValue.setFieldValue(findHeatingKey, findValue?.kcalM3);
      }
    });

    if (tmpManageCompound && tmpManageCompound?.length) {
      // console.log("Manage compound : ", tmpManageCompound);
      const resultHeatingVal = calculateHeatingValue(tmpManageCompound);
      formSetupHeatingValue.setFieldValue("netHeatingValue", resultHeatingVal);
      setManageCompound(tmpManageCompound);
    }
  };
  const renderAttachUnit = () => (
    <Form.Item name="unitHeatingValue" initialValue={UNITS_ENUM.KCAL_KG} style={{ height: "1.8rem", marginBottom: 0, padding: 0 }}>
      <Select style={{ width: 100 }} options={CompoundUnits} onChange={onChangeUnitHeatingValue} />
    </Form.Item>
  );

  return (
    <Modal
      title="Heating Value"
      open={visibleModal}
      width={1000}
      onCancel={closeModal}
      footer={
        <Row className="w-full" justify={"end"}>
          <Button key="submit" type="primary" loading={loadingManageHeating} onClick={submitSetupHeatingValue}>
            Save
          </Button>
        </Row>
      }
      maskClosable={false}
      centered
    >
      {errorHeating && (
        <Row className="w-full" justify={"center"}>
          <Text type="danger">{errorHeating}</Text>
        </Row>
      )}
      <Form
        layout={"vertical"}
        name={"setup-heating-value"}
        autoComplete="off"
        form={formSetupHeatingValue}
        requiredMark={false}
        onFieldsChange={(_) => {
          if (errorHeating) {
            clearErrorHeating("errorHeating");
            formSetupHeatingValue.setFields([
              {
                name: "gasName",
                errors: undefined,
              },
              {
                name: "composition",
                errors: undefined,
              },
            ]);
          }
        }}
      >
        <Row gutter={[16, 0]}>
          <Col xs={24} sm={12} lg={12}>
            <Form.Item
              label={<FormLabel label={"Gas Name"} required />}
              name="gasName"
              rules={[
                {
                  required: true,
                  message: generateErrorMessage("Gas Name"),
                },
              ]}
            >
              <Input maxLength={50} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} lg={12}>
            <Form.Item
              label={<FormLabel label={"Composition"} required />}
              name="composition"
              rules={[
                {
                  required: true,
                  message: generateErrorMessage("composition"),
                },
              ]}
            >
              <Select options={CompositionDropdown} onChange={(val) => onChangeComposition(val)} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} lg={12}>
            <Form.Item
              label={<FormLabel label={"Net Heating Value"} required />}
              name="netHeatingValue"
              rules={[
                {
                  required: true,
                  message: generateErrorMessage("Net Heating Value"),
                },
              ]}
            >
              <CustomInputNumber
                precision={2}
                addonAfter={renderAttachUnit()}
                disabled={composition && composition === Composition.SPECIFIC_COMPOSITION}
                formatter={(value) => formatNumber(value as number, 0)}
                onKeyDown={(e) => {
                  onKeyDownNumberWithFixedDecimal(e, 0);
                }}
              />
            </Form.Item>
          </Col>
        </Row>
        {composition && composition === Composition.SPECIFIC_COMPOSITION && (
          <Col>
            <Table
              columns={columns}
              dataSource={manageCompound}
              className="w-full"
              loading={loadingCompound}
              size={"small"}
              rowKey={(item) => `${item.key}`}
              pagination={false}
              footer={(dataTable) => {
                const totalValue = dataTable?.length
                  ? dataTable.reduce((accumulator, currentItem) => {
                      return accumulator + Number(currentItem.mole);
                    }, 0)
                  : 0;
                return (
                  <Row className="w-full" justify={"space-between"}>
                    <Text strong style={{ paddingLeft: "10rem" }}>
                      Total
                    </Text>
                    <Text strong style={{ paddingRight: "11rem" }}>
                      {parseFloat(`${totalValue}`).toFixed(2)}
                    </Text>
                  </Row>
                );
              }}
            />
            <Row className="w-full mt-4" justify={"end"}>
              <Button type={"primary"} onClick={onAddCompound}>
                <WrapCenter>
                  <PlusCircleOutlined />
                </WrapCenter>
              </Button>
            </Row>
          </Col>
        )}
      </Form>
    </Modal>
  );
};

export default HeatingModal;
